import { useLayoutEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { Icon, Tooltip, type IconName } from "@otrack/ui";
import { href, type Route } from "./router";

export interface Section {
  label: string;
  icon: IconName;
  route: Route;
  pages: Array<Route["page"]>;
}

interface Box { x: number; y: number; width: number; height: number }

/** One spring for the pill and the labels, so the Island moves as a single piece. */
const SPRING = { type: "spring", stiffness: 380, damping: 34, mass: 0.9 } as const;

/**
 * The Island's sections; only the current one spells its name. While the labels trade width the
 * real items are in motion, so the pill is measured against a hidden copy of the nav laid out in
 * its final state and glides straight to where the item will end up.
 */
export function SectionNav({ sections, page }: { sections: Section[]; page: Route["page"] }) {
  const active = sections.findIndex((section) => section.pages.includes(page));
  const ghostRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<Box | null>(null);

  useLayoutEffect(() => {
    const ghost = ghostRef.current;
    if (!ghost) return;
    const measure = () => {
      const item = ghost.children[active] as HTMLElement | undefined;
      const next = item ? { x: item.offsetLeft, y: item.offsetTop, width: item.offsetWidth, height: item.offsetHeight } : null;
      setPill((current) => current && next && current.x === next.x && current.y === next.y && current.width === next.width && current.height === next.height ? current : next);
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    // Fonts loading, the window resizing, or the phone dock taking over all move the target.
    const observer = new ResizeObserver(measure);
    for (const child of [ghost, ...ghost.children]) observer.observe(child);
    return () => observer.disconnect();
  }, [active]);

  return <nav className="ot-nav" aria-label="Sections">
    <div ref={ghostRef} className="ot-nav__ghost" aria-hidden="true">
      {sections.map((section, index) => <span key={section.label} className={`ot-nav__item ${index === active ? "is-on" : ""}`}>
        <Icon name={section.icon}/>
        <span className="ot-nav__label">{section.label}</span>
      </span>)}
    </div>
    {pill && <motion.span className="ot-nav__pill" aria-hidden="true" initial={false} animate={{ x: pill.x, y: pill.y, width: pill.width, height: pill.height }} transition={SPRING}/>}
    {sections.map((section, index) => {
      const on = index === active;
      // Every item stays mounted, tooltip included, so the labels can animate between sections.
      return <Tooltip key={section.label} label={section.label} placement="bottom" disabled={on}>
        <a href={href(section.route)} className={`ot-nav__item ${on ? "is-on" : ""}`} aria-current={on ? "page" : undefined}>
          <Icon name={section.icon}/>
          <motion.span
            className="ot-nav__label"
            initial={false}
            animate={on ? { width: "auto", marginLeft: 7, opacity: 1 } : { width: 0, marginLeft: 0, opacity: 0 }}
            transition={{ ...SPRING, opacity: { duration: on ? 0.28 : 0.14, delay: on ? 0.08 : 0 } }}
          >{section.label}</motion.span>
        </a>
      </Tooltip>;
    })}
  </nav>;
}
